import { useState, useRef, useEffect } from 'preact/hooks';
import {
  createSmartVideoThumbnail,
  formatTime,
  supportsBrowserCapture,
} from '../lib/mediaUtils';
import { trackCaptureEvent } from '../lib/captureAnalytics';
import { saveDraft, getDraft, deleteDraft, type VideoDraft } from '../lib/videoDraft';
import { CoverFramePicker } from './CoverFramePicker';
import styles from './VideoContribution.module.css';
import sharedStyles from './ContributionShared.module.css';

export interface VideoContributionResult {
  blob: Blob;
  duration: number;
  contentType: string;
  filename: string;
  thumbnailBlob?: Blob | null;
  thumbnailFilename?: string;
  width?: number;
  height?: number;
  coverFrameTimeMs?: number;
  coverSelectionMode?: 'auto' | 'manual';
  audioTrackDegraded?: boolean;
}

interface VideoContributionProps {
  onSubmit: (payload: VideoContributionResult) => void;
  submitting: boolean;
  threadId: string;
  promptText?: string;
  onUseFallbackCapture?: () => void;
  fallbackLabel?: string;
  onBack?: () => void;
}

function formatDraftAge(capturedAt: number): string {
  const ms = Date.now() - capturedAt;
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

type RecorderState = 'idle' | 'recording' | 'recorded' | 'permission-denied' | 'error' | 'unsupported';

const MAX_DURATION_SEC = 60;
const SIZE_WARNING_MB = 50;

function extensionForMimeType(mimeType: string): string {
  if (mimeType.includes('mp4')) return 'mp4';
  return 'webm';
}

export function VideoContribution({
  onSubmit,
  submitting,
  threadId,
  promptText,
  onUseFallbackCapture,
  fallbackLabel = 'Use Device Camera',
  onBack,
}: VideoContributionProps) {
  const [state, setState] = useState<RecorderState>(() => supportsBrowserCapture() ? 'idle' : 'unsupported');
  const [elapsed, setElapsed] = useState(0);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [sizeWarning, setSizeWarning] = useState(false);
  const [recordingMimeType, setRecordingMimeType] = useState('video/webm');
  const [thumbnailBlob, setThumbnailBlob] = useState<Blob | null>(null);
  const [coverFrameTimeMs, setCoverFrameTimeMs] = useState(0);
  const [coverSelectionMode, setCoverSelectionMode] = useState<'auto' | 'manual'>('auto');
  const [videoSize, setVideoSize] = useState<{ width?: number; height?: number }>({});
  const [posterUrl, setPosterUrl] = useState<string | null>(null);
  const [showReviewPlayOverlay, setShowReviewPlayOverlay] = useState(false);
  const [showCoverPicker, setShowCoverPicker] = useState(false);
  const [audioWarning, setAudioWarning] = useState<string | null>(null);
  const [audioTrackDegraded, setAudioTrackDegraded] = useState(false);
  const [countdownValue, setCountdownValue] = useState<number | null>(null);
  const [pendingDraft, setPendingDraft] = useState<VideoDraft | null>(null);

  const videoPreviewRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const videoUrlRef = useRef<string | null>(null);
  const posterUrlRef = useRef<string | null>(null);
  const audioTrackDegradedRef = useRef(false);
  const countdownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const recordingProgress = elapsed / MAX_DURATION_SEC;

  useEffect(() => {
    return () => {
      stopRecording();
      if (countdownTimerRef.current) {
        clearTimeout(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
      if (videoUrlRef.current) {
        URL.revokeObjectURL(videoUrlRef.current);
      }
      if (posterUrlRef.current) {
        URL.revokeObjectURL(posterUrlRef.current);
      }
    };
  }, []);

  // Check for existing draft on mount
  useEffect(() => {
    getDraft(threadId).then(draft => {
      if (draft) setPendingDraft(draft);
    }).catch(() => {});
  }, [threadId]);

  const persistDraft = (
    nextVideoBlob: Blob,
    nextThumbnailBlob: Blob | null,
    nextCoverFrameTimeMs: number,
    nextCoverSelectionMode: 'auto' | 'manual',
    nextDuration: number,
  ) => {
    saveDraft({
      threadId,
      videoBlob: nextVideoBlob,
      thumbnailBlob: nextThumbnailBlob,
      coverFrameTimeMs: nextCoverFrameTimeMs,
      coverSelectionMode: nextCoverSelectionMode,
      duration: nextDuration,
    }).catch(err => console.warn('Draft save failed:', err));
  };

  const restoreFromDraft = (draft: VideoDraft) => {
    if (videoUrlRef.current) {
      URL.revokeObjectURL(videoUrlRef.current);
      videoUrlRef.current = null;
    }
    if (posterUrlRef.current) {
      URL.revokeObjectURL(posterUrlRef.current);
      posterUrlRef.current = null;
    }
    setVideoBlob(draft.videoBlob);
    const nextUrl = URL.createObjectURL(draft.videoBlob);
    setVideoUrl(nextUrl);
    videoUrlRef.current = nextUrl;
    setThumbnailBlob(draft.thumbnailBlob);
    setCoverFrameTimeMs(draft.coverFrameTimeMs);
    setCoverSelectionMode(draft.coverSelectionMode);
    setElapsed(draft.duration);
    if (draft.thumbnailBlob) {
      const nextPosterUrl = URL.createObjectURL(draft.thumbnailBlob);
      posterUrlRef.current = nextPosterUrl;
      setPosterUrl(nextPosterUrl);
    }
    setState('recorded');
    setShowReviewPlayOverlay(true);
    setAudioTrackDegraded(false);
    setPendingDraft(null);
  };

  const startPreview = async (): Promise<MediaStream> => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });

    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) {
      setAudioWarning('We could not detect microphone audio. Check permissions and try again.');
      trackCaptureEvent('audio_track_lost', { stage: 'start_preview', reason: 'no_audio_tracks' });
    } else {
      audioTracks.forEach((track, index) => {
        track.onmute = () => {
          audioTrackDegradedRef.current = true;
          setAudioWarning('Microphone audio was interrupted during recording.');
          trackCaptureEvent('audio_track_lost', { index, reason: 'muted' });
        };
        track.onended = () => {
          audioTrackDegradedRef.current = true;
          setAudioWarning('Microphone audio ended during recording.');
          trackCaptureEvent('audio_track_lost', { index, reason: 'ended' });
        };
      });
    }

    streamRef.current = stream;
    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = stream;
      videoPreviewRef.current.muted = true;
      videoPreviewRef.current.play().catch(() => {});
    }
    return stream;
  };

  const startRecording = async () => {
    if (!supportsBrowserCapture()) {
      setState('unsupported');
      return;
    }

    try {
      audioTrackDegradedRef.current = false;
      setAudioTrackDegraded(false);
      setAudioWarning(null);
      const stream = streamRef.current || (await startPreview());

      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
        ? 'video/webm;codecs=vp9,opus'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
          ? 'video/webm;codecs=vp8,opus'
          : MediaRecorder.isTypeSupported('video/webm')
            ? 'video/webm'
            : 'video/mp4';

      const recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 2_500_000,
      });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      setRecordingMimeType(mimeType);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
        const nextUrl = URL.createObjectURL(blob);
        setVideoBlob(blob);
        setVideoUrl(nextUrl);
        videoUrlRef.current = nextUrl;
        setSizeWarning(blob.size > SIZE_WARNING_MB * 1024 * 1024);

        const previewElement = videoPreviewRef.current;
        if (previewElement) {
          setVideoSize({
            width: previewElement.videoWidth || undefined,
            height: previewElement.videoHeight || undefined,
          });
        }

        const thumbnail = await createSmartVideoThumbnail(blob);
        setThumbnailBlob(thumbnail.blob);
        setCoverFrameTimeMs(thumbnail.frameTimeMs);
        setCoverSelectionMode(thumbnail.selectionMode);

        if (posterUrlRef.current) {
          URL.revokeObjectURL(posterUrlRef.current);
          posterUrlRef.current = null;
        }
        if (thumbnail.blob) {
          const nextPosterUrl = URL.createObjectURL(thumbnail.blob);
          posterUrlRef.current = nextPosterUrl;
          setPosterUrl(nextPosterUrl);
          trackCaptureEvent('cover_auto_selected', { frameTimeMs: thumbnail.frameTimeMs });
        } else {
          setPosterUrl(null);
        }

        stream.getAudioTracks().forEach((track, index) => {
          trackCaptureEvent('capture_stop', {
            audioTrackIndex: index,
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState,
          });
        });

        if (audioTrackDegradedRef.current) {
          trackCaptureEvent('audio_track_degraded', { degraded: true });
        }
        setAudioTrackDegraded(audioTrackDegradedRef.current);

        setShowReviewPlayOverlay(true);
        setState('recorded');

        persistDraft(
          blob,
          thumbnail.blob,
          thumbnail.frameTimeMs,
          thumbnail.selectionMode,
          elapsed,
        );
      };

      recorder.start(1000);
      trackCaptureEvent('capture_start', { mimeType });
      setState('recording');
      setElapsed(0);

      timerRef.current = setInterval(() => {
        setElapsed((previous) => {
          const next = previous + 1;
          if (next >= MAX_DURATION_SEC) {
            stopRecording();
            return MAX_DURATION_SEC;
          }
          return next;
        });
      }, 1000);
    } catch (error: unknown) {
      const domError = error as DOMException;
      if (domError.name === 'NotAllowedError' || domError.name === 'PermissionDeniedError') {
        setState('permission-denied');
      } else {
        trackCaptureEvent('capture_error', { errorName: domError.name });
        console.error('[VideoContribution] getUserMedia failed:', error);
        setState('error');
      }
    }
  };

  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoPreviewRef.current) {
      videoPreviewRef.current.pause();
      videoPreviewRef.current.srcObject = null;
    }
  };

  const runCountdown = () => {
    let count = 3;
    setCountdownValue(count);
    const tick = () => {
      count--;
      if (count > 0) {
        setCountdownValue(count);
        countdownTimerRef.current = setTimeout(tick, 1000);
      } else {
        setCountdownValue(null);
        countdownTimerRef.current = null;
        startRecording();
      }
    };
    countdownTimerRef.current = setTimeout(tick, 1000);
  };

  const cancelCountdown = () => {
    if (countdownTimerRef.current) {
      clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdownValue(null);
  };

  const handleRecordClick = () => {
    if (countdownValue !== null) {
      cancelCountdown();
    } else if (state === 'recording') {
      stopRecording();
    } else {
      runCountdown();
    }
  };

  const discard = () => {
    if (videoUrlRef.current) {
      URL.revokeObjectURL(videoUrlRef.current);
    }
    if (posterUrlRef.current) {
      URL.revokeObjectURL(posterUrlRef.current);
    }
    videoUrlRef.current = null;
    posterUrlRef.current = null;
    setVideoBlob(null);
    setVideoUrl(null);
    setPosterUrl(null);
    setElapsed(0);
    setSizeWarning(false);
    setThumbnailBlob(null);
    setCoverFrameTimeMs(0);
    setCoverSelectionMode('auto');
    setVideoSize({});
    setShowReviewPlayOverlay(false);
    setAudioWarning(null);
    setAudioTrackDegraded(false);
    setState(supportsBrowserCapture() ? 'idle' : 'unsupported');

    // Delete any saved draft
    getDraft(threadId).then(draft => {
      if (draft) deleteDraft(draft.id);
    }).catch(() => {});
  };

  const handleSubmit = (event: Event) => {
    event.preventDefault();
    if (!videoBlob || submitting) return;

    const contentType = recordingMimeType || videoBlob.type || 'video/webm';
    onSubmit({
      blob: videoBlob,
      duration: elapsed,
      contentType,
      filename: `video-${Date.now()}.${extensionForMimeType(contentType)}`,
      thumbnailBlob,
      thumbnailFilename: thumbnailBlob ? `video-thumb-${Date.now()}.jpg` : undefined,
      width: videoSize.width,
      height: videoSize.height,
      coverFrameTimeMs,
      coverSelectionMode,
      audioTrackDegraded,
    });
  };

  const handleReviewPlay = async () => {
    const reviewVideo = videoPreviewRef.current;
    if (!reviewVideo) return;

    trackCaptureEvent('review_play_attempt', {});
    try {
      await reviewVideo.play();
      setShowReviewPlayOverlay(false);
    } catch {
      trackCaptureEvent('review_play_blocked', {});
      setShowReviewPlayOverlay(true);
    }
  };

  if (state === 'unsupported') {
    return (
      <div class={sharedStyles.permissionError}>
        <h3 class={sharedStyles.heading}>Browser recording isn&apos;t available here</h3>
        <p>
          Use your device camera instead, then attach the file back in the composer.
        </p>
        {onUseFallbackCapture && (
          <button type="button" onClick={onUseFallbackCapture}>{fallbackLabel}</button>
        )}
      </div>
    );
  }

  if (state === 'permission-denied') {
    return (
      <div class={sharedStyles.permissionError}>
        <h3 class={sharedStyles.heading}>Camera Access Required</h3>
        <p>
          We need camera and microphone access to record video. Please allow access in your browser
          settings and try again.
        </p>
        <div class={styles.fallbackActions}>
          <button type="button" onClick={() => setState('idle')}>Try Again</button>
          {onUseFallbackCapture && (
            <button type="button" onClick={onUseFallbackCapture}>{fallbackLabel}</button>
          )}
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div class={sharedStyles.errorState}>
        <h3 class={sharedStyles.heading}>Something Went Wrong</h3>
        <p>
          We couldn&apos;t access your camera or microphone. Please check that they are
          connected and try again.
        </p>
        <div class={styles.fallbackActions}>
          <button type="button" onClick={() => setState('idle')}>Try Again</button>
          {onUseFallbackCapture && (
            <button type="button" onClick={onUseFallbackCapture}>{fallbackLabel}</button>
          )}
        </div>
      </div>
    );
  }

  if (state === 'recorded' && videoUrl) {
    if (showCoverPicker && videoBlob) {
      return (
        <CoverFramePicker
          videoBlob={videoBlob}
          currentFrameTimeMs={coverFrameTimeMs}
          onSelect={({ blob, frameTimeMs, selectionMode }) => {
            setThumbnailBlob(blob);
            setCoverFrameTimeMs(frameTimeMs);
            setCoverSelectionMode(selectionMode);
            if (posterUrlRef.current) {
              URL.revokeObjectURL(posterUrlRef.current);
            }
            const nextPosterUrl = URL.createObjectURL(blob);
            posterUrlRef.current = nextPosterUrl;
            setPosterUrl(nextPosterUrl);
            if (videoBlob) {
              persistDraft(videoBlob, blob, frameTimeMs, selectionMode, elapsed);
            }
            setShowCoverPicker(false);
          }}
          onCancel={() => setShowCoverPicker(false)}
        />
      );
    }

    return (
      <form onSubmit={handleSubmit} class={sharedStyles.captureRoot}>
        {onBack && (
          <button type="button" class={`${sharedStyles.floatingBack} ${styles.floatingBack}`} onClick={onBack} aria-label="Go back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
        )}
        <h3 class={`${sharedStyles.heading} ${sharedStyles.headingWithBack}`}>Review Video</h3>
        <div class={styles.playbackArea}>
          <div class={styles.reviewVideoWrap}>
            <video
              ref={videoPreviewRef}
              src={videoUrl}
              controls
              playsInline
              preload="metadata"
              poster={posterUrl ?? undefined}
              class={styles.videoPlayback}
            />
            {showReviewPlayOverlay && (
              <button
                type="button"
                class={styles.reviewPlayOverlay}
                aria-label="Play recorded video"
                onClick={handleReviewPlay}
              >
                ▶
              </button>
            )}
          </div>
          <div class={styles.metaRow}>
            <span>{formatTime(elapsed)} / {formatTime(MAX_DURATION_SEC)}</span>
            <span>Video ready</span>
          </div>
          {audioWarning && <p class={styles.sizeWarning}>{audioWarning}</p>}
          {sizeWarning && (
            <p class={styles.sizeWarning}>
              This video is large and may take a while to upload on slower connections.
            </p>
          )}
          <div class={sharedStyles.actions}>
            <button type="button" class={sharedStyles.discardButton} onClick={discard}>
              Re-record
            </button>
            <button
              type="button"
              class={styles.editCoverButton}
              onClick={() => setShowCoverPicker(true)}
            >
              Edit Cover
            </button>
            <button type="submit" class={sharedStyles.submitButton} disabled={submitting}>
              {submitting ? 'Uploading...' : 'Use Video'}
            </button>
          </div>
        </div>
      </form>
    );
  }

  if (pendingDraft && state === 'idle') {
    const draftAgeText = formatDraftAge(pendingDraft.capturedAt);
    return (
      <div class={styles.draftPrompt}>
        <h3 class={sharedStyles.heading}>Unsaved Recording Found</h3>
        <p class={styles.draftPromptText}>You have an unsaved recording from {draftAgeText}</p>
        <div class={styles.draftActions}>
          <button
            type="button"
            class={sharedStyles.discardButton}
            onClick={() => {
              deleteDraft(pendingDraft.id).catch(() => {});
              setPendingDraft(null);
            }}
          >
            Discard
          </button>
          <button
            type="button"
            class={sharedStyles.submitButton}
            onClick={() => restoreFromDraft(pendingDraft)}
          >
            Restore
          </button>
        </div>
      </div>
    );
  }

  return (
    <div class={sharedStyles.captureRoot}>
      {onBack && (
        <button type="button" class={`${sharedStyles.floatingBack} ${styles.floatingBack}`} onClick={onBack} aria-label="Go back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
      )}
      {!onBack && <h3 class={sharedStyles.heading}>Record Video</h3>}
      <div class={styles.recorderArea}>
        <div class={styles.previewContainer}>
          <video
            ref={videoPreviewRef}
            playsInline
            muted
            class={styles.preview}
            style={{ display: state === 'recording' ? 'block' : 'none' }}
          />
          {state !== 'recording' && (
            <div class={styles.idlePlaceholder} />
          )}
          {state === 'recording' && (
            <>
              <div class={styles.recordingIndicator}>
                <span class={styles.recordingDot} />
                REC
              </div>
              <div class={styles.timerOverlay}>
                {formatTime(elapsed)} / {formatTime(MAX_DURATION_SEC)}
              </div>
              <div class={styles.progressBarContainer}>
                <div
                  class={styles.progressBar}
                  style={{
                    width: `${recordingProgress * 100}%`,
                    backgroundColor: recordingProgress < 0.6
                      ? 'var(--st-status-positive)'
                      : recordingProgress < 0.83
                        ? 'var(--st-status-caution)'
                        : 'var(--st-status-critical)',
                  }}
                />
              </div>
            </>
          )}
          {countdownValue !== null && (
            <div class={styles.countdownOverlay} role="status" aria-live="assertive">
              {/* key forces re-mount to re-trigger CSS animation */}
              <span class={styles.countdownNumber} key={countdownValue}>{countdownValue}</span>
            </div>
          )}
          {promptText && (
            <div class={styles.promptOverlay}>
              <span class={styles.promptIcon}>💡</span>
              <span class={styles.promptText}>{promptText}</span>
            </div>
          )}
        </div>

        <div class={styles.controls}>
          <span class={sharedStyles.srOnly} aria-live="assertive">
            {state === 'recording' ? 'Recording in progress' : ''}
          </span>

          <div class={styles.iconSpacer} />

          <button
            type="button"
            aria-label={
              countdownValue !== null
                ? 'Cancel countdown'
                : state === 'recording'
                  ? 'Stop recording video'
                  : 'Start recording video'
            }
            class={`${sharedStyles.recordButton} ${state === 'recording' ? sharedStyles.recording : ''}`}
            onClick={handleRecordClick}
          >
            {state === 'recording' ? (
              <span class={sharedStyles.stopIcon} />
            ) : (
              <span class={sharedStyles.recordIcon} />
            )}
          </button>

          <div class={styles.iconSpacer} />
        </div>

        <span class={sharedStyles.hint}>
          {countdownValue !== null
            ? 'Tap to cancel'
            : state === 'recording'
              ? 'Tap to stop'
              : 'Tap to start recording'}
        </span>
      </div>
    </div>
  );
}
